import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../../api.ts';
import type { Client } from './types.ts';

const ERRORS: Record<string, string> = {
  MISSING_FIELDS: 'Le nom du client est obligatoire.',
  INVALID_SIRET: 'Le SIRET doit comporter 14 chiffres.',
  INVALID_EMAIL: 'Adresse e-mail invalide.',
  CLIENT_IN_USE: 'Ce client a déjà des factures ou des recettes : il ne peut plus être supprimé, ni son SIRET modifié.',
};

const EMPTY = { name: '', siret: '', address: '', postalCode: '', city: '', email: '' };

export function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<{ clients: Client[] }>('/extra/clients').then((r) => setClients(r.clients));
  }, []);
  useEffect(load, [load]);

  function edit(c: Client) {
    setEditingId(c.id);
    setForm({ name: c.name, siret: c.siret ?? '', address: c.address ?? '', postalCode: c.postalCode ?? '', city: c.city ?? '', email: c.email ?? '' });
    setOpen(true);
    setError(null);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (editingId) await api.put(`/extra/clients/${editingId}`, form);
      else await api.post('/extra/clients', form);
      setForm(EMPTY);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Client) {
    if (!window.confirm(`Supprimer « ${c.name} » ?`)) return;
    setError(null);
    try {
      await api.delete(`/extra/clients/${c.id}`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Suppression impossible.' : 'Suppression impossible.');
    }
  }

  const set = (patch: Partial<typeof EMPTY>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Vos clients en dehors de l'app. Les établissements rencontrés via Mon Extra sont déjà comptés automatiquement. Saisissez le SIRET d'un établissement
        pour qu'il ne soit compté qu'une fois, même s'il vous a aussi engagé par l'app.
      </div>
      {error && <div className="error-banner">{error}</div>}

      {!open && <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }}>Ajouter un client</button>}

      {open && (
        <form onSubmit={save} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 800 }}>{editingId ? 'Modifier le client' : 'Nouveau client'}</div>
          <input className="input" required placeholder="Nom ou raison sociale" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          <input className="input" placeholder="SIRET (14 chiffres, facultatif pour un particulier)" value={form.siret} onChange={(e) => set({ siret: e.target.value })} />
          <input className="input" placeholder="Adresse" value={form.address} onChange={(e) => set({ address: e.target.value })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <input className="input" placeholder="Code postal" value={form.postalCode} onChange={(e) => set({ postalCode: e.target.value })} />
            <input className="input" placeholder="Ville" value={form.city} onChange={(e) => set({ city: e.target.value })} />
          </div>
          <input className="input" type="email" placeholder="E-mail (facultatif)" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={busy}>Enregistrer</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setEditingId(null); }}>Annuler</button>
          </div>
        </form>
      )}

      {clients.length === 0 && !open && <div className="empty-state">Aucun client hors app pour l'instant.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {clients.map((c) => (
          <div key={c.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800 }}>{c.name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {[c.siret && `SIRET ${c.siret}`, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || 'Aucune coordonnée'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => edit(c)}>Modifier</button>
              <button className="btn btn-danger-outline btn-sm" onClick={() => remove(c)}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
